/**
 * View state for the Results / Developer / File surfaces. OWNER: workstream F.
 *
 * TODO(workstream C): this is a *stand-in* for the parts of `@/store/appStore`
 * that were not available when these tabs were written. The pieces that belong
 * to the shell — which case is open, which ribbon tab is active, which job is
 * running — are reached through `shellBridge` below and through
 * `setActiveCase()`. When the global store lands, the wiring is:
 *
 *   setShellBridge({ navigateTab: useAppStore.getState().setActiveTab, ... });
 *   useAppStore.subscribe((s) => setActiveCase(s.caseManifest));
 *
 * Nothing else in this workstream reaches for shell state, so that is the whole
 * integration surface.
 */

import * as React from "react";
import { create } from "zustand";
import type { CaseManifest, JobSpec, ResultsSummary } from "@/ipc/contract";
import {
  exportResults,
  getAssetUrl,
  getResultsSummary,
  openCase,
  pickDirectory,
  revealPath,
  saveCase,
  splitError,
} from "@/ipc/api";

export type ExportFormat = "pdf" | "csv" | "nifti" | "json";

export const EXPORT_FORMATS: ExportFormat[] = ["pdf", "csv", "nifti", "json"];

/** Sections of the File backstage; also the deep-link target of `file.*` actions. */
export type FileSection =
  | "new"
  | "open"
  | "recent"
  | "save"
  | "saveAs"
  | "import"
  | "export"
  | "preferences"
  | "close"
  | "exit";

// -------------------------------------------------------- shell bridge ---

/**
 * The handful of shell operations these tabs need but do not own. Every method
 * is optional: unimplemented ones leave a notice in the UI instead of pretending
 * the action happened.
 */
export interface ShellBridge {
  /** Switch the ribbon to another tab id ("pipeline", "file", "results" ...). */
  navigateTab?: (tabId: string) => void;
  /** Start the New Case flow (Pipeline tab owns Case Setup). */
  newCase?: () => void;
  /** Hand a freshly opened case back to the shell so it becomes the active one. */
  caseOpened?: (manifest: CaseManifest) => void;
  /** Close the active case. */
  closeCase?: () => void;
  /** Files the user picked in File → Import. */
  importPaths?: (kind: ImportKind, paths: string[]) => void;
  /** Re-run the given stages of the active case (Pipeline tab owns the runner). */
  runStages?: (stages: string[]) => void;
  /** Log file of the job currently or most recently attached to this case. */
  currentJobLogPath?: () => string | null;
  /** Application log file, if the shell knows where it is. */
  appLogPath?: () => string | null;
}

export type ImportKind = "nifti" | "folder" | "mask";

let shellBridge: ShellBridge = {};

export function setShellBridge(bridge: ShellBridge): void {
  shellBridge = { ...shellBridge, ...bridge };
}

export function shell(): ShellBridge {
  return shellBridge;
}

// ---------------------------------------------------------------- store ---

type LoadState = "idle" | "loading" | "ready" | "error";

interface ResultsState {
  /** Active case. Set by the shell; null until a case is opened. */
  caseManifest: CaseManifest | null;
  setActiveCase: (manifest: CaseManifest | null) => void;

  summary: ResultsSummary | null;
  summaryState: LoadState;
  summaryError: string | null;
  summaryDetail: string | null;
  loadSummary: (force?: boolean) => Promise<void>;

  /** One-line message shown inline; used where an action belongs to another tab. */
  notice: string | null;
  setNotice: (notice: string | null) => void;

  exportFormats: Record<ExportFormat, boolean>;
  toggleExportFormat: (format: ExportFormat, value?: boolean) => void;
  exportDir: string | null;
  setExportDir: (dir: string | null) => void;
  chooseExportDir: () => Promise<void>;
  openAfterExport: boolean;
  setOpenAfterExport: (value: boolean) => void;
  exporting: boolean;
  exportWritten: string[] | null;
  exportError: string | null;
  runExport: (formats?: ExportFormat[]) => Promise<void>;

  /** Mirrors `CaseManifest.cleanupEnabled`; persisted through `save_case`. */
  cleanupEnabled: boolean;
  setCleanupEnabled: (value: boolean) => Promise<void>;

  fileSection: FileSection;
  setFileSection: (section: FileSection) => void;
}

export const useResultsStore = create<ResultsState>()((set, get) => ({
  caseManifest: null,
  setActiveCase: (manifest) => {
    const previous = get().caseManifest;
    set({
      caseManifest: manifest,
      cleanupEnabled: manifest ? manifest.cleanupEnabled : get().cleanupEnabled,
      // A different case means the loaded summary no longer applies.
      ...(manifest?.caseId !== previous?.caseId
        ? { summary: null, summaryState: "idle" as LoadState, summaryError: null, summaryDetail: null }
        : {}),
    });
  },

  summary: null,
  summaryState: "idle",
  summaryError: null,
  summaryDetail: null,
  loadSummary: async (force = false) => {
    const caseId = get().caseManifest?.caseId;
    if (!caseId) {
      set({ summary: null, summaryState: "idle", summaryError: null, summaryDetail: null });
      return;
    }
    if (!force && get().summaryState === "loading") return;
    set({ summaryState: "loading", summaryError: null, summaryDetail: null });
    try {
      const summary = await getResultsSummary(caseId);
      set({ summary, summaryState: "ready" });
    } catch (error) {
      const { message, detail } = splitError(error);
      set({ summary: null, summaryState: "error", summaryError: message, summaryDetail: detail });
    }
  },

  notice: null,
  setNotice: (notice) => set({ notice }),

  exportFormats: { pdf: true, csv: true, nifti: false, json: true },
  toggleExportFormat: (format, value) =>
    set((s) => ({
      exportFormats: { ...s.exportFormats, [format]: value ?? !s.exportFormats[format] },
    })),
  exportDir: null,
  setExportDir: (dir) => set({ exportDir: dir }),
  chooseExportDir: async () => {
    const start = get().exportDir ?? get().caseManifest?.outputRoot ?? undefined;
    const { path } = await pickDirectory("Select export directory", start);
    if (path) set({ exportDir: path });
  },
  openAfterExport: true,
  setOpenAfterExport: (value) => set({ openAfterExport: value }),
  exporting: false,
  exportWritten: null,
  exportError: null,
  runExport: async (formats) => {
    const state = get();
    const caseId = state.caseManifest?.caseId;
    if (!caseId) {
      set({ exportError: "Open a case before exporting." });
      return;
    }
    const selected = formats ?? EXPORT_FORMATS.filter((f) => state.exportFormats[f]);
    if (selected.length === 0) {
      set({ exportError: "Select at least one export format." });
      return;
    }
    let dir = state.exportDir;
    if (!dir) {
      const { path } = await pickDirectory("Select export directory", state.caseManifest?.outputRoot);
      if (!path) return;
      dir = path;
      set({ exportDir: path });
    }
    set({ exporting: true, exportError: null, exportWritten: null });
    try {
      const { written, warnings } = await exportResults(caseId, dir, selected);
      // Files that were copied stay copied. A warning here means one piece
      // could not be produced, not that the export did not happen.
      set({
        exporting: false,
        exportWritten: written,
        exportError: warnings.length ? warnings.join(" ") : null,
      });
      if (get().openAfterExport) {
        await revealPath(written[0] ?? dir);
      }
    } catch (error) {
      set({ exporting: false, exportError: splitError(error).message });
    }
  },

  cleanupEnabled: true,
  setCleanupEnabled: async (value) => {
    set({ cleanupEnabled: value });
    const manifest = get().caseManifest;
    if (!manifest) return;
    // The flag lives in case.json, so persist it rather than keeping a UI-only
    // toggle that silently disagrees with the next run.
    const next: CaseManifest = { ...manifest, cleanupEnabled: value };
    try {
      await saveCase(next);
      set({ caseManifest: next });
    } catch (error) {
      set({ cleanupEnabled: manifest.cleanupEnabled, notice: splitError(error).message });
    }
  },

  fileSection: "new",
  setFileSection: (section) => set({ fileSection: section }),
}));

// ------------------------------------------------------------- helpers ---

/** JobSpec for the active case — what `preview_command` needs to build the argv. */
export function currentJobSpec(): JobSpec | null {
  const manifest = useResultsStore.getState().caseManifest;
  if (!manifest) return null;
  return {
    caseId: manifest.caseId,
    pipelineKind: manifest.pipelineKind,
    stages: manifest.stages,
    gpuIndex: manifest.gpuIndex,
    dockerImage: manifest.dockerImage,
    cleanupEnabled: useResultsStore.getState().cleanupEnabled,
  };
}

/**
 * Dev-only seed so the Results and Developer tabs have a case to render before
 * the shell owns one. It asks the backend to open a case with no path, which a
 * real backend rejects; only the mock layer answers, and only in a dev build.
 */
export async function ensureDevCase(): Promise<void> {
  if (!import.meta.env.DEV) return;
  if (useResultsStore.getState().caseManifest) return;
  try {
    const manifest = await openCase("");
    if (!useResultsStore.getState().caseManifest) {
      useResultsStore.getState().setActiveCase(manifest);
    }
  } catch {
    // No mock, no case: the tabs render their empty states, which is correct.
  }
}

/** Loads the summary for the active case and keeps it fresh across case changes. */
export function useResultsSummary(): {
  summary: ResultsSummary | null;
  state: LoadState;
  error: string | null;
  detail: string | null;
  reload: () => void;
} {
  const caseId = useResultsStore((s) => s.caseManifest?.caseId ?? null);
  const summary = useResultsStore((s) => s.summary);
  const state = useResultsStore((s) => s.summaryState);
  const error = useResultsStore((s) => s.summaryError);
  const detail = useResultsStore((s) => s.summaryDetail);
  const loadSummary = useResultsStore((s) => s.loadSummary);

  React.useEffect(() => {
    void ensureDevCase();
  }, []);

  React.useEffect(() => {
    if (caseId && useResultsStore.getState().summaryState === "idle") void loadSummary();
  }, [caseId, loadSummary]);

  const reload = React.useCallback(() => void loadSummary(true), [loadSummary]);
  return { summary, state, error, detail, reload };
}

/**
 * Resolves a host path to something an `<img>` can load. `get_asset_url` does the
 * path-traversal check on the Rust side; the mock returns "", which is why the
 * callers all render a fallback tile when the URL is empty.
 */
export function useAssetUrl(path: string | null): string | null {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    if (!path) {
      setUrl(null);
      return;
    }
    getAssetUrl(path)
      .then((r) => {
        if (!cancelled) setUrl(r.url || null);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);
  return url;
}
